import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n/i18n';

export const MAX_COMPARE = 3;

/** Lightweight snapshot for the sticky compare tray (full product fetched on compare page). */
export type CompareSelectionItem = {
  _id: string;
  productName: string;
  productImage?: string;
  imageVersion?: number;
  articleNumber?: string | null;
};

type CompareStore = {
  items: CompareSelectionItem[];
  minimized: boolean;
  add: (item: CompareSelectionItem) => boolean;
  remove: (id: string) => void;
  toggle: (item: CompareSelectionItem) => boolean;
  clear: () => void;
  setItems: (items: CompareSelectionItem[]) => void;
  setMinimized: (minimized: boolean) => void;
  isSelected: (id: string) => boolean;
};

export const useCompareStore = create<CompareStore>()(
  persist(
    (set, get) => ({
      items: [],
      minimized: false,
      add: (item) => {
        const { items } = get();
        if (!item._id || items.some((i) => i._id === item._id)) return false;
        if (items.length >= MAX_COMPARE) return false;
        set({ items: [...items, item] });
        return true;
      },
      remove: (id) => {
        const next = get().items.filter((i) => i._id !== id);
        set({ items: next, minimized: next.length === 0 ? false : get().minimized });
      },
      toggle: (item) => {
        const { items, add, remove } = get();
        if (items.some((i) => i._id === item._id)) {
          remove(item._id);
          return false;
        }
        return add(item);
      },
      clear: () => set({ items: [], minimized: false }),
      setItems: (next) => {
        const seen = new Set<string>();
        const items: CompareSelectionItem[] = [];
        for (const item of next) {
          if (!item._id || seen.has(item._id)) continue;
          seen.add(item._id);
          items.push(item);
          if (items.length >= MAX_COMPARE) break;
        }
        set({ items, minimized: items.length === 0 ? false : get().minimized });
      },
      setMinimized: (minimized) => set({ minimized }),
      isSelected: (id) => get().items.some((i) => i._id === id),
    }),
    { name: 'compare-selection' }
  )
);

export function toCompareSelectionItem(product: {
  _id?: string;
  productName?: string;
  productImage?: string;
  imageVersion?: number;
  articleNumber?: string | null;
}): CompareSelectionItem | null {
  if (!product._id) return null;
  return {
    _id: product._id,
    productName: product.productName || i18n.t('marketplace.compare.fallbackProductName'),
    productImage: product.productImage,
    imageVersion: product.imageVersion,
    articleNumber: product.articleNumber ?? null,
  };
}
